// API bindings: projects (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { ProjectListResponse, ProjectResponse, ProjectCreateRequest, ProjectUpdateRequest } from "./definitions";

export class ApiProjects {
    /**
     * Method: GET
     * Path: /projects
     * Lists projects.
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListProjects(queryParams: ListProjectsQueryParameters): RequestParams<ProjectListResponse, ListProjectsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/projects` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_CERTIFICATE_URL", handler.badRequestInvalidCertificateUrl)
                    .add(400, "INVALID_URL", handler.badRequestInvalidUrl)
                    .add(400, "INVALID_COLOR", handler.badRequestInvalidColor)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /projects
     * Creates a project.
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateProject(body: ProjectCreateRequest): RequestParams<ProjectResponse, CreateProjectErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/projects`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_CERTIFICATE_URL", handler.badRequestInvalidCertificateUrl)
                    .add(400, "INVALID_URL", handler.badRequestInvalidUrl)
                    .add(400, "INVALID_COLOR", handler.badRequestInvalidColor)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /projects/{id}
     * Gets a project.
     * @param id Project identifier
     * @returns The request parameters
     */
    public static GetProject(id: string): RequestParams<ProjectResponse, GetProjectErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/projects/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /projects/{id}
     * Updates a project.
     * @param id Project identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static UpdateProject(id: string, body: ProjectUpdateRequest): RequestParams<ProjectResponse, UpdateProjectErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/projects/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_CERTIFICATE_URL", handler.badRequestInvalidCertificateUrl)
                    .add(400, "INVALID_URL", handler.badRequestInvalidUrl)
                    .add(400, "INVALID_COLOR", handler.badRequestInvalidColor)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /projects/{id}
     * Deletes a project.
     * @param id Project identifier
     * @returns The request parameters
     */
    public static DeleteProject(id: string): RequestParams<void, DeleteProjectErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/projects/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /projects/{id}/certificate-check/run
     * Runs the configured HTTPS certificate check immediately.
     * @param id Project identifier
     * @returns The request parameters
     */
    public static RunProjectCertificateCheck(id: string): RequestParams<ProjectResponse, RunProjectCertificateCheckErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/projects/${encodeURIComponent(id)}/certificate-check/run`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "CERTIFICATE_CHECK_DISABLED", handler.badRequestCertificateCheckDisabled)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for ListProjects
 */
export type ListProjectsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The project name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The project description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The project color is missing or is not a valid hexadecimal color
     */
    badRequestInvalidColor: () => void;

    /**
     * The project URL is not a valid HTTP or HTTPS address
     */
    badRequestInvalidUrl: () => void;

    /**
     * The certificate monitoring URL is not a valid HTTPS address or is required when enabled
     */
    badRequestInvalidCertificateUrl: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;
};

/**
 * Query parameters for ListProjects
 */
export interface ListProjectsQueryParameters {
    /**
     * Page number, starting at 1.
     */
    page?: number;

    /**
     * Number of projects per page, maximum 100.
     */
    pageSize?: number;
}

/**
 * Error handler for CreateProject
 */
export type CreateProjectErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The project name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The project description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The project color is missing or is not a valid hexadecimal color
     */
    badRequestInvalidColor: () => void;

    /**
     * The project URL is not a valid HTTP or HTTPS address
     */
    badRequestInvalidUrl: () => void;

    /**
     * The certificate monitoring URL is not a valid HTTPS address or is required when enabled
     */
    badRequestInvalidCertificateUrl: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;
};

/**
 * Error handler for GetProject
 */
export type GetProjectErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for UpdateProject
 */
export type UpdateProjectErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * The request contains fields that cannot be changed
     */
    badRequestInvalidFields: () => void;

    /**
     * The request contains values with invalid types
     */
    badRequestInvalidBody: () => void;

    /**
     * The project name is missing, empty or too long
     */
    badRequestInvalidName: () => void;

    /**
     * The project description is too long
     */
    badRequestInvalidDescription: () => void;

    /**
     * The project color is missing or is not a valid hexadecimal color
     */
    badRequestInvalidColor: () => void;

    /**
     * The project URL is not a valid HTTP or HTTPS address
     */
    badRequestInvalidUrl: () => void;

    /**
     * The certificate monitoring URL is not a valid HTTPS address or is required when enabled
     */
    badRequestInvalidCertificateUrl: () => void;

    /**
     * The update request contains no editable fields
     */
    badRequestNoFields: () => void;

    /**
     * The pagination parameters are invalid
     */
    badRequestInvalidPagination: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteProject
 */
export type DeleteProjectErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for RunProjectCertificateCheck
 */
export type RunProjectCertificateCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = CERTIFICATE_CHECK_DISABLED
     */
    badRequestCertificateCheckDisabled: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};
